// 「快捷」— 原「一鍵場景」獨立軸，2026-08-19 業主裁決（R5 收斂保守案 §3.B）併入警戒卡：
// 場景回答的是「家裡現在要多警覺／調成什麼樣子」，與模式同一個問題，不該是家頁的另一個軸。
// 後端與 CRUD 完全不動（仍是 /v3/scenes，＝ trigger=manual 的 automation 門面），改的只有
// 位置與稱呼。零場景時**整塊不渲染**——原本的空狀態提示（home.noScenes）是純噪音：web 沒有
// 場景編輯器，那行字指不到任何使用者做得到的動作。

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import { applyScene, fetchScenes, type Scene } from './scenesApi';

export function ScenesPanel() {
  const { t } = useTranslation();
  const [scenes, setScenes] = useState<Scene[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);

  const load = useCallback((signal?: AbortSignal) => {
    setError(null);
    void fetchScenes(30, signal).then(
      (rows) => setScenes(rows),
      (err: unknown) => {
        if (signal?.aborted) return;
        const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
        setError(msg);
        setScenes([]);
      }
    );
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  async function run(scene: Scene) {
    setApplying(scene.scene_id);
    setApplied(null);
    setError(null);
    try {
      await applyScene(scene.scene_id);
      setApplied(scene.scene_id);
      load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setApplying(null);
    }
  }

  const loading = scenes === null;

  // 載入中、或沒有任何場景且無錯誤 → 不佔版面（無空狀態文案）。
  if (loading || (scenes.length === 0 && !error)) return null;

  return (
    <section className="home-quick" aria-labelledby="home-quick-h">
      <div className="home-quick-head">
        <h3 id="home-quick-h">{t('home.quickActions')}</h3>
        <button className="btn-ghost" type="button" onClick={() => load()}>
          {t('common.refresh')}
        </button>
      </div>

      {error && <p className="error-text home-scenes-error">{t('home.loadScenesFailed', { error })}</p>}
      {scenes.length > 0 && (
        <div className="home-scene-strip" role="list">
          {scenes.map((scene) => {
            const busy = applying === scene.scene_id;
            const justApplied = applied === scene.scene_id;
            return (
              <article className="home-scene-card" role="listitem" key={scene.scene_id}>
                <div>
                  <div className="home-scene-name">{scene.name}</div>
                  <div className="text-secondary home-scene-meta">
                    {t('home.sceneItems', { count: scene.items.length })}
                    {justApplied ? ` · ${t('home.sceneApplied')}` : ''}
                  </div>
                </div>
                <button className="btn-primary home-scene-apply" type="button" disabled={!scene.enabled || busy} onClick={() => void run(scene)}>
                  {busy ? t('home.sceneApplying') : t('home.sceneApply')}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
